import {format} from 'date-fns';
import frLocale from 'date-fns/locale/fr';
import packageJson from '../../package.json';
import {Client, EntrepriseCorpsEtat, Lot, LotVisite, Operation, Reserve, User} from "./types/Types";
import ApiHelper from "../api/ApiHelper";
import {generatePdfDocument} from "./PDFRenderer";

export class CommonHelper {

    static includesWithoutAccents(keyword: string, text: string = '') {
        return CommonHelper.removeAccents(text).toUpperCase().includes(CommonHelper.removeAccents(keyword).toUpperCase());
    }

    static removeAccents(text: string) {
        return text.replace(/[áàãâä]/gi, "a")
            .replace(/[éè¨ê]/gi, "e")
            .replace(/[íìïî]/gi, "i")
            .replace(/[óòöôõ]/gi, "o")
            .replace(/[úùüû]/gi, "u")
            .replace(/[ç]/gi, "c")
            .replace(/[ñ]/gi, "n")
            .replace(/[^a-zA-Z0-9]/g, " ");
    }

    static formatDate(dateString: string | Date, formatString = 'dd/MM/yyyy') {
        if (!dateString) {
            return '';
        }
        return format(new Date(dateString), formatString, {locale: frLocale});
    }

    static dateStringToTime(dateString?: string | Date) {
        return dateString ? new Date(dateString).getTime() : 0;
    }

    static formatModeObtentionAdlr(refModeObtentionAdlr: number) {
        switch (refModeObtentionAdlr) {
            case 0:
                return ' '
            case 1:
                return 'De fait';
            case 2:
                return 'Att. LR';
            case 3:
                return 'LVQ';
            default:
                return '';
        }
    }

    static formatAcquereur(acquereur: Client | User) {
        return acquereur ? [acquereur.prenom, acquereur.nom].join(' ') : '';
    }

    static formatEntrepriseCorpEtat(ec: EntrepriseCorpsEtat) {
        return `${ec.entreprise?.nom} / ${ec.corpsEtat?.libCorpsEtat}`;
    }

    static formatLot(lot: Lot) {
        if (lot.acquereur) {
            return `${lot.numeroLot} / ${CommonHelper.formatAcquereur(lot.acquereur)}`;
        }

        return `${lot.numeroLot}`;
    }

    static formatOperation(o: Operation) {
        return `${o.nomOperation} (${o.codeUc}-${o.codeCi})`;
    }

    static getRemoteReservePhotoUrl(reserve: Reserve) {
        if (!reserve.photos.length) {
            return '/img/placeholder.png';
        }

        const photo = reserve.photos[reserve.photos.length - 1];

        if (photo.nomFichier?.includes('PHOTO_REZERV')) {
            return `https://rezerv.bouygues-immobilier.com/Photos/${photo.nomFichier}`;
        } else if (photo.urlImgRezerv?.includes('http')) {
            return photo.urlImgRezerv;
        } else {
            return `${ApiHelper.getStaticUrl()}/${photo.urlImgRezerv}`;
        }
    }

    static getBuildVersion() {
        return packageJson.version;
    }

    static getApiDocUrl() {
        return ApiHelper.getApiDocUrl();
    }

    static completeLotVisiteDates(lot: Lot) {
        lot.visiteCloison = lot.lotVisites.find(visite => visite.phaseRefPhase === 7)?.dateLotVisite;
        lot.visiteOpr = lot.lotVisites.find(visite => visite.phaseRefPhase === 8)?.dateLotVisite;
        lot.visitePreLivraison = lot.lotVisites.find(visite => visite.phaseRefPhase === 6)?.dateLotVisite;
        lot.visiteLivraison = lot.lotVisites.find(visite => visite.phaseRefPhase === 5)?.dateLotVisite;
    }

    static completeVisitesDatesForAPI(lot: Lot, values: any) {
        lot.locataire = values.locataire;
        lot.refModeObtentionAdlr = values.refModeObtentionAdlr;
        lot.dateObtentionLvadlr = values.dateObtentionLvadlr;
        lot.dateEnvoiLvadlr = values.dateEnvoiLvadlr;
        lot.dateRefusAdlr = values.dateRefusAdlr;
        lot.commentaire = values.commentaire;

        const visiteCloison = lot.lotVisites.find(visite => visite.phaseRefPhase === 7);
        if (values.dateVisiteCloison && !visiteCloison) {
            lot.lotVisites.push({
                dateLotVisite: values.dateVisiteCloison,
                phaseRefPhase: 7,
                lotRefLot: lot.refLot
            } as LotVisite);
        } else if (visiteCloison) {
            visiteCloison.dateLotVisite = values.dateVisiteCloison;
        }
        const visiteOpr = lot.lotVisites.find(visite => visite.phaseRefPhase === 8);
        if (values.dateVisiteOpr && !visiteOpr) {
            lot.lotVisites.push({
                dateLotVisite: values.dateVisiteOpr,
                phaseRefPhase: 8,
                lotRefLot: lot.refLot
            } as LotVisite);
        } else if (visiteOpr) {
            visiteOpr.dateLotVisite = values.dateVisiteOpr;
        }
        const visitePreLivraison = lot.lotVisites.find(visite => visite.phaseRefPhase === 6);
        if (values.dateVisitePreLivraison && !visitePreLivraison) {
            lot.lotVisites.push({
                dateLotVisite: values.dateVisitePreLivraison,
                phaseRefPhase: 6,
                lotRefLot: lot.refLot
            } as LotVisite);
        } else if (visitePreLivraison) {
            visitePreLivraison.dateLotVisite = values.dateVisitePreLivraison;
        }
        const visiteLivraison = lot.lotVisites.find(visite => visite.phaseRefPhase === 5);
        if (values.dateVisiteLivraison && !visiteLivraison) {
            lot.lotVisites.push({
                dateLotVisite: values.dateVisiteLivraison,
                phaseRefPhase: 5,
                lotRefLot: lot.refLot
            } as LotVisite);
        } else if (visiteLivraison) {
            visiteLivraison.dateLotVisite = values.dateVisiteLivraison;
        }

    }

    static downloadBlob(blob: Blob, fileName: string) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('href', url);
        a.setAttribute('download', fileName);
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    static downloadCSV(data: string[][], fileName: string) {
        const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const csv = data.map(row => row.map(f => `"${f}"`).join(';')).join('\n');
        const blob = new Blob([BOM, csv], {type: 'text/csv;charset=utf-8'});

        CommonHelper.downloadBlob(blob, fileName);
    }

    static async downloadPDF(data: string[][], fileName: string, title: string) {
        const blob = await generatePdfDocument(data, title);

        CommonHelper.downloadBlob(blob, fileName);
    }

}
